import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import styled from "styled-components";
import {
  ACQUISITION_CODE_CHOICES,
  BANK_ACCOUNT_CHOICES,
  CHANNEL_CHOICES,
  CO_MANUFACTURING_ORDER_STATUS_CHOICES,
  convertToIdName,
  COUNTRIES_CHOICES,
  CUSTOMER_SEGMENT_STATUS_CHOICES,
  CUSTOMER_STATUS_CHOICES,
  CUSTOMER_SUB_TYPE_CHOICES,
  CUSTOMER_TYPE_CHOICES,
  DELIVERY_STATUS_CHOICES,
  IMPACT_CHOICES,
  LANDED_COST_TYPE_CHOICES,
  LEG_TYPE_CHOICES,
  MANUFACTURING_ORDER_STATUS_CHOICES,
  PACK_SCOPE_OPTIONS,
  PAYMENT_TERM_TYPE_CHOICES,
  PICKLIST_STATUS_CHOICES,
  PICKLIST_TYPE_CHOICES,
  PLANNING_POLICY_CHOICES,
  PREPAYMENT_STATUS_CHOICES,
  PREPAYMENT_TYPE_CHOICES,
  PRICELIST_STATUS_CHOICES,
  PRODUCT_STATUS_CHOICES,
  PRODUCT_TYPE_CHOICES,
  PROMO_STATUS_CHOICES,
  PURCHASE_ORDER_PRIORITY,
  QUOTATION_STATUS_CHOICES,
  REPLENISHMENT_STATUS,
  SHIPMENT_LEG_STATUS_CHOICES,
  SHIPMENT_STATUS_CHOICES,
  STOCK_ZONE_TYPE_PRESETS,
  stockZoneTypeLabel,
  TRANSPORT_MODE_CHOICES,
  UOM_CATEGORY_CHOICES,
  WAREHOUSE_TYPE_CHOICES,
  warehouseTypeLabel,
} from "../../../../../../constant/data";
import { useGetBillChargeTypesSelectionQuery } from "../../../../../../store/apiSlice/invoiceApiSlice";
import { useLazyGetPurchaseOrdersSelectionQuery } from "../../../../../../store/apiSlice/purchaseOrderApiSlice";
import { useGetSalesRepFilterOptionsQuery } from "../../../../../../store/apiSlice/salesApiSlice";
import {
  useGetSitesSelectionQuery,
  useGetWarehouseStockZoneTypesQuery,
} from "../../../../../../store/apiSlice/warehouseApiSlice";
import { BILL_STATUS } from "../../../../../bill/layout/constant";
import {
  EXETERNAL_SYSTEM_CHOICES,
  SYNC_HISTORY_STATUS,
} from "../../../../../integrationV1/constant";
import { DISCOUNT_TYPE_OPTIONS } from "../../../../../promo/manage/constants";
import { PURCHASE_ORDER_PAYMENT_STATUS_OPTIONS } from "../../../../../purchaseOrder/constants/paymentStatusOptions";
import {
  SALE_INVOICE_FULFILLMENT_FILTER_OPTIONS,
  SALE_INVOICE_FULFILLMENT_HEADERS,
  SALE_INVOICE_STATUS,
} from "../../../../../saleInvoice/layout/constant";
import { ReactComponent as Check } from "../../../../assets/svgIcon/listView/CheckForFilter.svg";
import { ReactComponent as Close } from "../../../../assets/svgIcon/listView/Close.svg";
import { ReactComponent as Search } from "../../../../assets/svgIcon/listView/search.svg";
import { PopupModal } from "../../../../components/TopBar/PopupModal/PopupModal";
import {
  getZeroFilterParam,
  qtyHeaders,
  selectFilterFieldForHeader,
  serverFieldForQty,
} from "../../../utils/tableDataElements";
import { BOOLEAN_FIELD_FOR_FILTER, queryParamsName, selectedHeadersForQueryParams } from "../util";
import { useGetDataForFilter } from "./Hook/useGetDataForFilter";
import {
  purchaseOrderIdsFromNameOptions,
  purchaseOrderNameOptions,
} from "./purchaseOrderNameOptions";

/**
 * Filter options for consolidated receipt / receipt-adjustment overall_status.
 *
 * @const
 * @type {Array<{name: string, id: string}>}
 */
const RECEIPT_OVERALL_STATUS_FILTER_OPTIONS = [
  { name: "Approved", id: "Approved" },
  { name: "Partially Approved", id: "Partially Approved" },
  { name: "Created", id: "Created" },
  { name: "Cancelled", id: "Cancelled" },
];

const BILL_CHARGE_AMOUNT_TYPE_CHOICES = [
  { id: "exclusive", name: "Exclusive" },
  { id: "inclusive", name: "Inclusive" },
  { id: "no_tax", name: "No Tax" },
];

const BILL_CHARGE_DISTRIBUTION_TYPE_CHOICES = [
  { id: "value", name: "Standard" },
  { id: "volume", name: "Volume" },
  { id: "volumetric_weight", name: "Volumetric Weight" },
  { id: "weight", name: "Weight" },
];

export function FilterModal({
  setIsAppliedFilter,
  headerName,
  closeFilterModal,
  setConsignmentsParamsLocal,
  positionForFilter,
  orderType,
  allocation,
}) {
  const searchBoxRef = useRef(null);
  const widthOfSearchedOrder = useRef(null);
  const orderNameListOpened = useRef(false);
  const [isSearchedItemRender, setIsSearchedItemRender] = useState(false);
  const [isSelectedAllSearchOrder, setIsSelectedAllSearchOrder] = useState(false);
  const [selectedSearchOrder, setSelectedSearchOrder] = useState([]);

  useGetDataForFilter({ headerName, orderType, allocation });

  const isAllocationOrderName =
    orderType === "allocation" && allocation === "purchase" && headerName === "order name";
  const [loadAllocationOrderNames, allocationOrderNameQuery] =
    useLazyGetPurchaseOrdersSelectionQuery();

  useEffect(() => {
    if (!isAllocationOrderName) return;
    loadAllocationOrderNames(new URLSearchParams({ allocatable: "1", limit: "500" }).toString());
  }, [isAllocationOrderName, loadAllocationOrderNames]);

  const { data: salesRepFilterOptions = [] } = useGetSalesRepFilterOptionsQuery(undefined, {
    skip: !(orderType === "salesRep" && headerName === "full name"),
  });
  const { data: salesRepCreatorFilterOptions = [] } = useGetSalesRepFilterOptionsQuery(
    "created_by",
    { skip: !(orderType === "salesRep" && headerName === "created by") },
  );
  const { data: billChargeTypesSelectionQueryData } = useGetBillChargeTypesSelectionQuery(
    undefined,
    {
      skip: !(orderType === "billChargeType" && headerName === "name"),
    },
  );

  const { data: stockZoneTypesData } = useGetWarehouseStockZoneTypesQuery(null, {
    skip: !(orderType === "stockZone" && headerName === "type"),
  });

  const { data: warehouseSitesSelection } = useGetSitesSelectionQuery(null, {
    skip: !(orderType === "warehouse" && headerName === "site"),
  });

  const {
    accountTypeSelection,
    billChargeTypesSelection,
    billsSelection,
    chartsAccountSelection,
    customerSegmentSelection,
    consignmentStatus,
    consignmentsSelectionV2,
    currenciesSelection,
    customerSelection,
    paymentTermSelection,
    prePaymentSelection,
    priceListsSelection,
    productTags,
    productCategories,
    products,
    productAliasSelection,
    productCategoriesSelection,
    productAlias,
    productsSelection,
    productTagsSelection,
    productTaxClassificationSelection,
    promosSelection,
    purchaseOrders,
    purchaseOrdersSelection,
    purchaseOrderStatusV2,
    receiptsSelection,
    receiptAdjustmentSelection,
    stockLineStatus,
    stockLocationsSelection,
    stockReplenishmentSelection,
    stockZonesSelection,
    sitesSelection,
    workCentersSelection,
    workCenterTypes,
    supplier,
    supplierCreditNotesSelection,
    saleInvoiceSelection,
    taxJurisdictionSelection,
    taxRateSelection,
    transactionReasonsSelection,
    transferOrderLineStatus,
    transferOrdersSelection,
    transferOrderStatus,
    users,
    warehouse,
    warehouses,
    taxCategorySelection,
    customerTagsSelection,
    unitOfMeasureSelection,
    equipmentTypesSelection,
    equipmentsSelection,
    equipmentTypeCategories,
    quotationSelection,
    salesOrderSelection,
    picklistSelection,
    stockPacksSelection,
    deliverySelection,
    asnSelection,
    asnList,
    shipmentsV1Selection,
    customerReturnSelection,
    shipmentLegCarriersSelection,
    shipmentLegOriginsSelection,
    shipmentLegDestinationsSelection,
    shipmentLegDeliverTosSelection,
    shipmentLegVehicleReferencesSelection,
    shipmentLegSequencesSelection,
    peopleSelection,
    bomsSelection,
    manufacturingOrderSelection,
    coManufacturingOrderSelection,
    peopleRoles,
    countriesSelection,
    suppliersSelection,
  } = useSelector(
    ({
      account: { chartsAccountSelection, accountTypeSelection, paymentTermSelection },
      currency: { currenciesSelection },
      config: { countriesSelection },
      customer: { customerSelection, customerTagsSelection, customerSegmentSelection },
      invoice: {
        billChargeTypesSelection,
        billsSelection,
        prePaymentSelection,
        supplierCreditNotesSelection,
        saleInvoiceSelection,
      },
      order: { transferOrderLineStatus, transferOrdersSelection, transferOrderStatus },
      product: {
        productCategories,
        productTags,
        productsSelection,
        productTagsSelection,
        productAliasSelection,
        productCategoriesSelection,
        productAlias,
      },
      purchaseOrder: { purchaseOrders, purchaseOrdersSelection, purchaseOrderStatusV2 },
      receipt: { receiptsSelection, receiptAdjustmentSelection },
      report: { products, supplier, transactionReasonsSelection, users, warehouse },
      shipment: {
        asnList,
        asnSelection,
        consignmentStatus,
        consignmentsSelectionV2,
        deliverySelection,
        equipmentTypesSelection,
        equipmentsSelection,
        equipmentTypeCategories,
        shipmentsV1Selection,
        shipmentLegCarriersSelection,
        shipmentLegOriginsSelection,
        shipmentLegDestinationsSelection,
        shipmentLegDeliverTosSelection,
        shipmentLegVehicleReferencesSelection,
        shipmentLegSequencesSelection,
      },
      tax: {
        taxJurisdictionSelection,
        taxRateSelection,
        taxCategorySelection,
        productTaxClassificationSelection,
      },
      unitOfMeasure: { unitOfMeasureSelection },
      warehouse: {
        stockLineStatus,
        stockZonesSelection,
        stockLocationsSelection,
        stockReplenishmentSelection,
        picklistSelection,
        stockPacksSelection,
        sitesSelection,
        workCentersSelection,
        workCenterTypes,
      },
      sales: { quotationSelection, salesOrderSelection, customerReturnSelection },
      supplier: { warehouses, suppliersSelection },
      pricing: { priceListsSelection },
      promo: { promosSelection },
      production: { peopleSelection, peopleRoles, bomsSelection, manufacturingOrderSelection },
      manufacturingOrder: { coManufacturingOrderSelection },
    }) => ({
      accountTypeSelection,
      asnList,
      asnSelection,
      billsSelection,
      chartsAccountSelection,
      customerSegmentSelection,
      consignmentStatus,
      consignmentsSelectionV2,
      currenciesSelection,
      customerSelection,
      paymentTermSelection,
      prePaymentSelection,
      productCategories,
      productTags,
      products,
      productsSelection,
      purchaseOrders,
      purchaseOrdersSelection,
      productAliasSelection,
      productCategoriesSelection,
      productTagsSelection,
      productAlias,
      productTaxClassificationSelection,
      purchaseOrderStatusV2,
      receiptsSelection,
      receiptAdjustmentSelection,
      stockReplenishmentSelection,
      stockLineStatus,
      stockLocationsSelection,
      stockZonesSelection,
      sitesSelection,
      workCentersSelection,
      workCenterTypes,
      supplier,
      supplierCreditNotesSelection,
      saleInvoiceSelection,
      taxJurisdictionSelection,
      taxRateSelection,
      taxCategorySelection,
      customerTagsSelection,
      transactionReasonsSelection,
      transferOrderLineStatus,
      transferOrdersSelection,
      transferOrderStatus,
      users,
      unitOfMeasureSelection,
      equipmentTypesSelection,
      equipmentsSelection,
      equipmentTypeCategories,
      warehouse,
      warehouses,
      manufacturingOrderSelection,
      priceListsSelection,
      promosSelection,
      quotationSelection,
      salesOrderSelection,
      customerReturnSelection,
      picklistSelection,
      stockPacksSelection,
      deliverySelection,
      shipmentsV1Selection,
      shipmentLegCarriersSelection,
      shipmentLegOriginsSelection,
      shipmentLegDestinationsSelection,
      shipmentLegDeliverTosSelection,
      shipmentLegVehicleReferencesSelection,
      shipmentLegSequencesSelection,
      peopleSelection,
      bomsSelection,
      peopleRoles,
      countriesSelection,
      coManufacturingOrderSelection,
      suppliersSelection,
    }),
    shallowEqual,
  );

  const constantOptions = useCallback(arr => {
    return arr.map(item => ({ id: item.id, name: item.name }));
  }, []);

  /** Convert paginated Redux resources to their row array before using them as filter options. */
  const asRows = rows => {
    if (Array.isArray(rows)) return rows;
    return Array.isArray(rows?.results) ? rows.results : [];
  };

  /** Helper: keep only the first row for each unique `key` value */
  const uniqueBy = (rows, key) => {
    const seen = new Set();
    return asRows(rows).filter(r => {
      const val = r[key];
      if (!val || seen.has(val)) return false; // skip empty / duplicate
      seen.add(val);
      return true;
    });
  };

  const consignmentsSelection = useMemo(() => {
    if (orderType === "purchase" && headerName === "order name") {
      return purchaseOrderNameOptions([
        ...asRows(purchaseOrdersSelection),
        ...asRows(purchaseOrders),
      ]);
    }
    if (orderType === "purchase" && headerName === "shipments") {
      return asRows(consignmentsSelectionV2)
        .filter(it => it?.name)
        .map(it => ({ id: it.id, name: it.name }));
    }
    if (orderType === "allocation" && allocation === "purchase" && headerName === "order name") {
      return purchaseOrderNameOptions(asRows(allocationOrderNameQuery.data));
    }
    if (headerName === "name" && orderType === "shipment") {
      return consignmentsSelectionV2
        .filter(item => item["reference"])
        ?.map(it => ({ id: it.id, name: it.reference }));
    } else if (orderType === "shipmentV1" && ["shipment no", "status"].includes(headerName)) {
      switch (headerName) {
        case "shipment no":
          return shipmentsV1Selection;
        case "status":
          return SHIPMENT_STATUS_CHOICES;
        default:
          return [];
      }
    } else if (orderType === "asn" && headerName === "asn no") {
      const rows = asRows(asnSelection?.length ? asnSelection : asnList);
      return rows.filter(it => it?.name).map(it => ({ id: it.name, name: it.name }));
    } else if (orderType === "asn" && headerName === "status") {
      return [
        { id: "created", name: "Created" },
        { id: "confirmed", name: "Confirmed" },
        { id: "partially_received", name: "Partially Received" },
        { id: "received", name: "Received" },
        { id: "cancelled", name: "Cancelled" },
      ];
    } else if (orderType === "asn" && headerName === "shipment no") {
      return asRows(consignmentsSelectionV2)
        .filter(it => it?.name)
        .map(it => ({
          id: it.name,
          name: it.name,
        }));
    } else if (headerName === "shipment no") {
      return consignmentsSelectionV2;
    } else if (orderType === "salesRep" && headerName === "rep status") {
      return [
        { id: "pending", name: "Pending" },
        { id: "active", name: "Active" },
        { id: "inactive", name: "Inactive" },
      ];
    } else if (orderType === "salesRep" && headerName === "full name") {
      return salesRepFilterOptions;
    } else if (orderType === "salesRep" && headerName === "created by") {
      return salesRepCreatorFilterOptions;
    } else if (
      ["supplier"].includes(headerName) ||
      (orderType === "supplier" && ["name", "code"].includes(headerName))
    ) {
      if (orderType === "supplier" && headerName === "code") {
        return uniqueBy(supplier || [], "code")?.map(sp => ({
          name: sp.code,
          code: sp.code,
          id: sp.id,
        }));
      } else if (headerName === "code")
        return supplier?.map(sp => ({ id: sp.id, name: sp.code, code: sp.code }));
      return supplier;
    } else if (
      [
        "facility",
        "from facility",
        "to facility",
        "default facility",
        "supplying facility",
        "supply facility",
      ].includes(headerName)
    ) {
      return warehouse;
    } else if (headerName === "status" && orderType === "transfer") {
      return transferOrderStatus;
    } else if (headerName === "status" && orderType === "purchase") {
      return purchaseOrderStatusV2;
    } else if (headerName === "type" && orderType === "purchase") {
      return [
        { id: "standard", name: "Standard" },
        { id: "consolidated", name: "Consolidated" },
        { id: "dropship", name: "Dropship" },
      ];
    } else if (orderType === "purchase" && headerName === "payment status") {
      return PURCHASE_ORDER_PAYMENT_STATUS_OPTIONS;
    } else if (
      orderType === "purchase" &&
      ["po total", "payments", "outstanding"].includes(headerName)
    ) {
      const fieldByHeader = {
        "po total": "total_amount",
        payments: "total_payments_allocated",
        outstanding: "payment_outstanding",
      };
      const field = fieldByHeader[headerName];
      const seen = new Set();
      return asRows(purchaseOrders).reduce((options, order) => {
        const raw = order[field];
        if (raw == null || raw === "") {
          return options;
        }
        const id = String(raw);
        if (seen.has(id)) {
          return options;
        }
        seen.add(id);
        options.push({ id, name: id });
        return options;
      }, []);
    } else if (headerName === "status" && orderType === "allocation") {
      return transferOrderLineStatus;
    } else if (headerName === "status" && orderType === "inventoryDetail") {
      return stockLineStatus;
    } else if (headerName === "status" && orderType === "shipment") {
      return consignmentStatus;
    } else if (headerName === "status" && orderType === "bill") {
      return BILL_STATUS;
    } else if (
      SALE_INVOICE_FULFILLMENT_HEADERS.includes(headerName) &&
      orderType === "saleInvoice"
    ) {
      return SALE_INVOICE_FULFILLMENT_FILTER_OPTIONS;
    } else if (headerName === "status" && orderType === "saleInvoice") {
      return SALE_INVOICE_STATUS;
    } else if (headerName === "status" && orderType === "prePayment") {
      return PREPAYMENT_STATUS_CHOICES;
    } else if (headerName === "status" && orderType === "stockPack") {
      return [
        { id: "created", name: "Created" },
        { id: "packed", name: "Packed" },
        { id: "rfd", name: "Ready For Dispatch" },
        { id: "cancelled", name: "Cancelled" },
      ];
    } else if (headerName === "manf no") {
      return manufacturingOrderSelection?.map(item => ({ id: item.name, name: item.name }));
    } else if (headerName === "pack no") {
      return stockPacksSelection;
    } else if (orderType === "picklist" && headerName === "status") {
      return PICKLIST_STATUS_CHOICES;
    } else if (orderType === "picklist" && headerName === "type") {
      return PICKLIST_TYPE_CHOICES;
    } else if (
      headerName === "product" ||
      headerName === "product name" ||
      headerName === "description"
    ) {
      return products;
    } else if (
      orderType === "billChargeType" &&
      [
        "name",
        "amount type",
        "distribution type",
        "is landed cost",
        "is landed cost only",
      ].includes(headerName)
    ) {
      switch (headerName) {
        case "name":
          return asRows(billChargeTypesSelectionQueryData ?? billChargeTypesSelection);
        case "amount type":
          return BILL_CHARGE_AMOUNT_TYPE_CHOICES;
        case "distribution type":
          return BILL_CHARGE_DISTRIBUTION_TYPE_CHOICES;
        case "is landed cost":
        case "is landed cost only":
          return BOOLEAN_FIELD_FOR_FILTER;
        default:
          return [];
      }
    } else if (orderType === "unitOfMeasure") {
      switch (headerName) {
        case "name":
          return unitOfMeasureSelection.map(it => ({ id: it.name, name: it.name }));
        case "code":
          return unitOfMeasureSelection.map(it => ({ id: it.code, name: it.code, code: it.code }));
        case "category":
          return UOM_CATEGORY_CHOICES;
        case "system default":
          return [
            { id: true, name: "Standard" },
            { id: false, name: "Custom" },
          ];
        default:
          return [];
      }
    } else if (orderType === "equipmentType") {
      switch (headerName) {
        case "name":
          return equipmentTypesSelection?.map(it => ({ id: it.name, name: it.name }));
        case "code":
          return equipmentTypesSelection?.map(it => ({
            id: it.code,
            name: it.code,
            code: it.code,
          }));
        case "category":
          return equipmentTypeCategories;
        default:
          return [];
      }
    } else if (orderType === "equipment" && ["equipment type", "number"].includes(headerName)) {
      switch (headerName) {
        case "equipment type":
          return equipmentTypesSelection?.map(it => ({
            id: it?.id,
            name: it?.name,
          }));
        case "number":
          return equipmentsSelection?.map(it => ({
            id: it.number,
            name: it.number,
            number: it.number,
          }));
        default:
          return [];
      }
    } else if (
      ["ord no", "purchase no", "transfer no", "purchase orders", "transfer orders"].includes(
        headerName,
      )
    ) {
      return allocation === "transfer" ||
        headerName === "transfer no" ||
        headerName === "transfer orders"
        ? transferOrdersSelection
        : purchaseOrdersSelection;
    } else if (headerName === "category") {
      return asRows(productCategories);
    } else if (
      headerName === "reason" ||
      (headerName === "code" && orderType === "transactionReason")
    ) {
      return transactionReasonsSelection;
    } else if (headerName === "erp supplier") {
      const uniqueSuppliers = [...new Set(transferOrdersSelection.map(item => item.erp_supplier))];
      return uniqueSuppliers.map(supplier => ({ id: supplier, name: supplier }));
    } else if (headerName === "acquisition type") {
      return ACQUISITION_CODE_CHOICES;
    } else if (headerName === "planning policy") {
      return PLANNING_POLICY_CHOICES;
    } else if (headerName === "other costs type") {
      return LANDED_COST_TYPE_CHOICES;
    } else if (headerName === "impact") {
      return IMPACT_CHOICES;
    } else if (orderType === "shopifyCatalogue") {
      switch (headerName) {
        case "product":
          return uniqueBy(products || productsSelection || [], "number").map(item => ({
            id: item.number || item.id,
            name: item.number || item.name,
          }));
        case "status":
          return [
            { id: "true", name: "Listed" },
            { id: "false", name: "Not Listed" },
          ];
        case "available stock":
          return [
            { id: "zero", name: "Zero" },
            { id: "non-zero", name: "Non zero" },
          ];
        case "pricelist":
          return (priceListsSelection || []).map(item => ({
            id: item.name,
            name: item.name,
          }));
        case "allow backorder":
          return [
            { id: "true", name: "On" },
            { id: "false", name: "Off" },
          ];
        case "facilities":
          return (warehouse?.results || warehouse || []).map(item => ({
            id: item.name,
            name: item.name,
          }));
        default:
          return [];
      }
    } else if (orderType === "syncHistory" && headerName === "status") {
      return SYNC_HISTORY_STATUS;
    } else if (orderType === "syncHistory" && headerName === "application") {
      return EXETERNAL_SYSTEM_CHOICES;
    } else if (
      orderType === "receiptAdjustment" &&
      ["receipt adjustment no"].includes(headerName)
    ) {
      return receiptAdjustmentSelection;
    } else if (orderType === "receiptAdjustment" && ["receipt no"].includes(headerName)) {
      return receiptsSelection;
    } else if (getZeroFilterParam(orderType, headerName)) {
      return [
        { name: "Zero", id: "zero" },
        { name: "Non zero", id: "non-zero" },
      ];
    } else if (orderType === "inventory" && headerName === "categories") {
      return uniqueBy(productCategories, "name");
    } else if (qtyHeaders.includes(headerName)) {
      const options = [
        { name: "Zero", id: "zero" },
        { name: "Non zero", id: "non-zero" },
      ];
      return options;
    } else if (
      headerName === "receipt status" ||
      (["receipt", "receiptAdjustment"].includes(orderType) && headerName === "status")
    ) {
      return RECEIPT_OVERALL_STATUS_FILTER_OPTIONS;
    } else if (orderType === "receipt") {
      switch (headerName) {
        case "document number":
          return uniqueBy(receiptsSelection, "document_number");

        case "receipt no":
          return receiptsSelection;

        case "shipment":
          return consignmentsSelectionV2;

        case "shipment number":
          return uniqueBy(receiptsSelection, "shipment_number");

        default:
          return [];
      }
    } else if (headerName === "consolidation status") {
      const consolidatedStatus = [
        "Fully Consolidated",
        "Partially Consolidated",
        "Not Consolidated",
      ];
      return consolidatedStatus.map(status => ({ name: status, id: status }));
    } else if (orderType === "product") {
      switch (headerName) {
        case "categories":
          return uniqueBy(productCategories, "name");

        case "number":
          return uniqueBy(productsSelection, "number");

        case "name":
          return uniqueBy(productsSelection, "name");

        case "type":
          return constantOptions(PRODUCT_TYPE_CHOICES);

        case "status":
          return constantOptions(PRODUCT_STATUS_CHOICES);

        case "tags":
          return uniqueBy(productTagsSelection, "name")?.map(pt => ({ tags: pt.name, ...pt }));

        case "created by":
          return users;

        default:
          return [];
      }
    } else if (
      headerName === "created by" ||
      (orderType === "salesRep" && headerName === "full name") ||
      headerName === "sales rep"
    ) {
      return users;
    } else if (
      orderType === "paymentTerm" &&
      ["name", "term type", "due days", "month offset"].includes(headerName)
    ) {
      switch (headerName) {
        case "name":
          return paymentTermSelection;
        case "term type":
          return PAYMENT_TERM_TYPE_CHOICES;

        case "due days":
          return Array(31)
            .fill(null)
            .map((_, i) => ({ id: i + 1, name: i + 1 }));

        case "month offset":
          return Array(13)
            .fill(null)
            .map((_, i) => ({ id: i, name: i }));

        default:
          return [];
      }
    } else if (orderType === "productCategory") {
      return productCategoriesSelection;
    } else if (orderType === "productTag") {
      return productTagsSelection;
    } else if (orderType === "customerTag" && ["name"].includes(headerName)) {
      return customerTagsSelection;
    } else if (
      orderType === "warehouse" &&
      ["name", "code", "types", "site"].includes(headerName)
    ) {
      const warehouseOptions = warehouse?.results || warehouse || [];
      switch (headerName) {
        case "name":
          return warehouseOptions;

        case "code":
          return warehouseOptions?.map(pt => ({ id: pt.code, code: pt.code }));

        case "types":
          return WAREHOUSE_TYPE_CHOICES.map(pt => ({
            id: pt.id,
            name: warehouseTypeLabel(pt.id),
          }));

        case "site": {
          const list = Array.isArray(warehouseSitesSelection)
            ? warehouseSitesSelection
            : warehouseSitesSelection?.results || [];
          return list.map(pt => ({ id: pt.id, name: pt.name }));
        }

        default:
          return [];
      }
    } else if (orderType === "productAlias") {
      switch (headerName) {
        case "name":
          return productAliasSelection?.map(pt => ({ id: pt.id, name: pt.name }));

        case "number":
          return productAliasSelection?.map(pt => ({ id: pt.id, name: pt.number }));
        default:
          return [];
      }
    } else if (["buying currency", "selling currency", "currency"].includes(headerName)) {
      return currenciesSelection;
    } else if (["name", "type"].includes(headerName) && orderType === "stockZone") {
      switch (headerName) {
        case "name":
          return stockZonesSelection?.map(pt => ({ id: pt.id, name: pt.name }));

        case "type": {
          const list = Array.isArray(stockZoneTypesData)
            ? stockZoneTypesData
            : stockZoneTypesData?.results || STOCK_ZONE_TYPE_PRESETS;
          return list.map(pt => ({
            id: pt.id,
            name: stockZoneTypeLabel(pt.id),
          }));
        }

        default:
          return [];
      }
    } else if (
      ["name", "code", "stock zone"].includes(headerName) &&
      orderType === "stockLocation"
    ) {
      switch (headerName) {
        case "name":
          return uniqueBy(stockLocationsSelection, "name").map(pt => ({
            id: pt.name,
            name: pt.name,
          }));

        case "type":
          return uniqueBy(stockLocationsSelection, "code").map(pt => ({
            id: pt.code,
            name: pt.code,
          }));
        case "stock zone":
          return stockZonesSelection;

        default:
          return [];
      }
    } else if (
      orderType === "chartsOfAccount" ||
      (orderType === "prePayment" && ["account"].includes(headerName))
    ) {
      if (["name", "code", "account"].includes(headerName)) return chartsAccountSelection;
      else if (headerName === "account type") {
        return accountTypeSelection;
      }
      return [];
    } else if (orderType === "journalLine") {
      switch (headerName) {
        case "account code":
          return (chartsAccountSelection || []).map(item => ({
            id: item.id,
            name: item.code,
            code: item.code,
          }));
        case "account name":
          return chartsAccountSelection;
        default:
          return [];
      }
    } else if (["tax category name", "tax category"].includes(headerName)) {
      return taxCategorySelection;
    } else if (orderType === "taxRate" && ["name", "jurisdiction"].includes(headerName)) {
      switch (headerName) {
        case "name":
          return taxRateSelection;

        case "jurisdiction":
          return taxJurisdictionSelection;

        default:
          return [];
      }
    } else if (orderType === "site" && ["name", "code"].includes(headerName)) {
      return sitesSelection.map(it => ({ ...it, id: it[headerName] }));
    } else if (
      orderType === "workCenter" &&
      ["name", "code", "type", "location", "facility", "stock zone"].includes(headerName)
    ) {
      if (headerName === "type") {
        return workCenterTypes;
      } else if (headerName === "location") {
        return stockLocationsSelection;
      } else if (headerName === "facility") {
        return warehouse;
      } else if (headerName === "stock zone") {
        return stockZonesSelection;
      }
      return workCentersSelection.map(it => ({ ...it, id: it[headerName] }));
    } else if (orderType === "people" && ["name", "role"].includes(headerName)) {
      switch (headerName) {
        case "name":
          return peopleSelection.map(it => ({ name: it.full_name, id: it.full_name }));
        case "role":
          return peopleRoles;
        default:
          return [];
      }
    } else if (orderType === "users" && ["role", "status"].includes(headerName)) {
      switch (headerName) {
        case "role":
          return [
            { id: 1, name: "Admin" },
            { id: 2, name: "Member" },
            { id: 3, name: "Editor" },
            { id: 4, name: "Viewer" },
          ];
        case "status":
          return [
            { id: "Active", name: "Active" },
            { id: "Inactive", name: "Inactive" },
            { id: "Invited", name: "Invited" },
          ];
        default:
          return [];
      }
    } else if (orderType === "boms" && ["bom no", "type", "status"].includes(headerName)) {
      switch (headerName) {
        case "bom no":
          return (bomsSelection || []).map(it => ({ id: it.name, name: it.name }));
        case "type":
          return [
            { id: "standard", name: "Standard" },
            { id: "co-manufacturing", name: "Co Manufacturing" },
          ];
        case "status":
          return [
            { id: "draft", name: "Draft" },
            { id: "active", name: "Active" },
            { id: "inactive", name: "Inactive" },
          ];
        default:
          return [];
      }
    } else if (orderType === "manufacturingOrder" && ["mo no", "status"].includes(headerName)) {
      switch (headerName) {
        case "mo no":
          return (manufacturingOrderSelection || []).map(it => ({ id: it.name, name: it.name }));

        case "status":
          return MANUFACTURING_ORDER_STATUS_CHOICES;
        default:
          return [];
      }
    } else if (orderType === "coManufacturingOrder") {
      switch (headerName) {
        case "co-manf no":
          return (coManufacturingOrderSelection || []).map(it => ({ id: it.name, name: it.name }));
        case "co-manf":
          return suppliersSelection || [];
        case "co-manf whs":
        case "destination whs":
          return warehouse?.results || warehouse || [];
        case "priority":
          return PURCHASE_ORDER_PRIORITY;
        case "created by":
          return users;
        case "status":
          return CO_MANUFACTURING_ORDER_STATUS_CHOICES;
        default:
          return [];
      }
    } else if (orderType === "bill" && ["bill no", "supplier reference"].includes(headerName)) {
      if (headerName === "bill no") {
        return billsSelection?.map(bill => ({ id: bill.name, name: bill.name }));
      } else {
        return uniqueBy(billsSelection, "reference_no").map(bill => ({
          id: bill.reference_no,
          name: bill.reference_no,
        }));
      }
    } else if (
      orderType === "supplierCreditNote" &&
      ["credit no", "supplier reference"].includes(headerName)
    ) {
      if (headerName === "credit no") {
        return supplierCreditNotesSelection?.map(note => ({ id: note.name, name: note.name }));
      } else {
        return uniqueBy(supplierCreditNotesSelection, "reference_no").map(note => ({
          id: note.reference_no,
          name: note.reference_no,
        }));
      }
    } else if (orderType === "saleInvoice" && ["invoice no"].includes(headerName)) {
      if (headerName === "invoice no") {
        return saleInvoiceSelection?.map(invoice => ({ id: invoice.name, name: invoice.name }));
      }
    } else if (["bank account"].includes(headerName)) {
      return BANK_ACCOUNT_CHOICES;
    } else if (
      orderType === "prePayment" &&
      ["payment no", "prepayment no", "reference", "payment type"].includes(headerName)
    ) {
      if (headerName === "payment no" || headerName === "prepayment no") {
        return prePaymentSelection?.map(pre => ({ id: pre.name, name: pre.name }));
      } else if (headerName === "reference") {
        return uniqueBy(prePaymentSelection, "reference").map(pre => ({
          id: pre.reference,
          name: pre.reference,
        }));
      } else if (headerName === "payment type") {
        return PREPAYMENT_TYPE_CHOICES;
      }
    } else if (
      orderType === "stockReplenishment" &&
      ["status", "replenish no"].includes(headerName)
    ) {
      switch (headerName) {
        case "status":
          return REPLENISHMENT_STATUS;
        case "replenish no":
          return stockReplenishmentSelection;
        default:
          return [];
      }
    } else if (orderType === "customer") {
      switch (headerName) {
        case "display name":
          return uniqueBy(customerSelection, "display_name").map(it => ({
            id: it.display_name,
            name: it.display_name,
          }));
        case "type":
          return CUSTOMER_TYPE_CHOICES;
        case "company id":
          return uniqueBy(customerSelection, "company")
            .filter(it => it.company != null)
            .map(it => ({
              id: it.company.name,
              name: it.company.name,
            }));
        case "code":
          return uniqueBy(customerSelection, "customer_code").map(it => ({
            id: it.customer_code,
            name: it.customer_code,
            code: it.customer_code,
          }));
        case "default tax rate":
          return taxRateSelection;
        case "status":
          return CUSTOMER_STATUS_CHOICES;
        case "email":
          return uniqueBy(customerSelection, "email").map(it => ({ id: it.email, name: it.email }));
        case "created by":
          return users;
        case "sales rep":
          return users;
        case "sub type":
          return CUSTOMER_SUB_TYPE_CHOICES;
        case "tags":
          return customerTagsSelection;
        case "default payment term":
          return paymentTermSelection;
        case "default sale account":
          return chartsAccountSelection;
        default:
          return [];
      }
    } else if (orderType === "customerSegment") {
      switch (headerName) {
        case "customer segment name":
          return customerSegmentSelection.map(it => ({ id: it.name, name: it.name }));
        case "status":
          return CUSTOMER_SEGMENT_STATUS_CHOICES;
        default:
          return [];
      }
    } else if (headerName === "picklist no") {
      return picklistSelection.map(({ name }) => ({ name, id: name }));
    } else if (headerName === "product tax classification name") {
      return productTaxClassificationSelection;
    } else if (
      (headerName === "system default" &&
        ["taxRate", "taxCategory", "productTaxClassification"].includes(orderType)) ||
      (headerName === "co manufacturer" && orderType === "supplier")
    ) {
      return BOOLEAN_FIELD_FOR_FILTER;
    } else if (
      headerName === "default" ||
      (headerName === "active" && orderType === "paymentTerm")
    ) {
      return BOOLEAN_FIELD_FOR_FILTER;
    } else if (headerName === "scope" && orderType === "pack") {
      return PACK_SCOPE_OPTIONS;
    } else if (headerName === "sales no") {
      return salesOrderSelection;
    } else if (orderType === "pricelist") {
      switch (headerName) {
        case "pricelist name":
          return priceListsSelection?.map(it => ({ id: it.name, name: it.name }));
        case "country":
          return COUNTRIES_CHOICES;
        case "channel":
          return CHANNEL_CHOICES;
        case "status":
          return PRICELIST_STATUS_CHOICES;
        default:
          return [];
      }
    } else if (["status", "discount type", "title"] && orderType === "promo") {
      switch (headerName) {
        case "status":
          return PROMO_STATUS_CHOICES;
        case "discount type":
          return convertToIdName(DISCOUNT_TYPE_OPTIONS);
        case "title":
          return promosSelection?.map(it => ({ id: it.id, name: it.code || it.title }));
        default:
          return [];
      }
    } else if (["customer", "customers"].includes(headerName)) {
      return uniqueBy(customerSelection || [], "id").map(it => ({
        id: it.id,
        name: it.display_name || it.name,
      }));
    } else if (["sales orders"].includes(headerName)) {
      return salesOrderSelection;
    } else if (
      orderType === "quote" &&
      ["quote number", "status", "customer"].includes(headerName)
    ) {
      switch (headerName) {
        case "quote number":
          return quotationSelection;
        case "status":
          return QUOTATION_STATUS_CHOICES;
        default:
          return [];
      }
    } else if (orderType === "salesOrder") {
      switch (headerName) {
        case "sales no":
          return salesOrderSelection;
        case "status":
          return QUOTATION_STATUS_CHOICES;
        case "facility":
          return warehouse;
        case "pricelist":
          return priceListsSelection?.map(it => ({ id: it.name, name: it.name })) || [];
        case "salesperson":
          return users;
        case "currency":
          return currenciesSelection;
        default:
          return [];
      }
    } else if (orderType === "customerReturn") {
      switch (headerName) {
        case "return no":
          return customerReturnSelection?.map(it => ({ id: it.name, name: it.name }));
        default:
          return [];
      }
    } else if (headerName === "country" && orderType === "warehouse") {
      return countriesSelection;
    } else if (orderType === "delivery") {
      switch (headerName) {
        case "delivery no":
          return deliverySelection.map(it => ({ id: it.name, name: it.name }));
        case "status":
          return DELIVERY_STATUS_CHOICES;
        default:
          return [];
      }
    } else if (orderType === "shipmentLeg") {
      switch (headerName) {
        case "shipment name":
          return shipmentsV1Selection;
        case "type":
          return LEG_TYPE_CHOICES;
        case "status":
          return SHIPMENT_LEG_STATUS_CHOICES;
        case "carrier":
          return shipmentLegCarriersSelection?.results || shipmentLegCarriersSelection;
        case "origin":
          return shipmentLegOriginsSelection?.results || shipmentLegOriginsSelection;
        case "destination":
          return shipmentLegDestinationsSelection?.results || shipmentLegDestinationsSelection;
        case "deliver to":
          return shipmentLegDeliverTosSelection?.results || shipmentLegDeliverTosSelection;
        case "vehicle / vessel":
          return (
            shipmentLegVehicleReferencesSelection?.results || shipmentLegVehicleReferencesSelection
          );
        case "sequence":
          return shipmentLegSequencesSelection?.results || shipmentLegSequencesSelection;
        case "transport mode":
          return TRANSPORT_MODE_CHOICES;
        default:
          return [];
      }
    }

    return [];

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accountTypeSelection,
    asnList,
    asnSelection,
    billsSelection,
    customerSegmentSelection,
    chartsAccountSelection,
    consignmentsSelectionV2,
    currenciesSelection,
    customerSelection,
    supplier,
    transferOrdersSelection,
    paymentTermSelection,
    prePaymentSelection,
    purchaseOrders,
    purchaseOrdersSelection,
    purchaseOrderStatusV2,
    productTaxClassificationSelection,
    warehouse,
    transferOrderLineStatus,
    transferOrderStatus,
    consignmentStatus,
    salesRepFilterOptions,
    salesRepCreatorFilterOptions,
    users,
    products,
    stockLineStatus,
    stockLocationsSelection,
    stockReplenishmentSelection,
    stockZonesSelection,
    billChargeTypesSelection,
    billChargeTypesSelectionQueryData,
    supplierCreditNotesSelection,
    saleInvoiceSelection,
    productCategories,
    receiptsSelection,
    taxJurisdictionSelection,
    taxRateSelection,
    taxCategorySelection,
    transactionReasonsSelection,
    productsSelection,
    productCategoriesSelection,
    productTagsSelection,
    productAliasSelection,
    productAlias,
    productCategories,
    productTags,
    customerTagsSelection,
    warehouses,
    priceListsSelection,
    quotationSelection,
    salesOrderSelection,
    picklistSelection,
    stockPacksSelection,
    deliverySelection,
    equipmentTypesSelection,
    equipmentsSelection,
    equipmentTypeCategories,
    shipmentsV1Selection,
    warehouseSitesSelection,
    shipmentLegCarriersSelection,
    shipmentLegOriginsSelection,
    shipmentLegDestinationsSelection,
    shipmentLegDeliverTosSelection,
    shipmentLegVehicleReferencesSelection,
    shipmentLegSequencesSelection,
    peopleSelection,
    peopleRoles,
    countriesSelection,
    manufacturingOrderSelection,
    coManufacturingOrderSelection,
    bomsSelection,
    suppliersSelection,
    allocationOrderNameQuery.data,
    headerName,
    orderType,
    allocation,
  ]);

  const [searchedOrder, setSearchedOrder] = useState(consignmentsSelection || []);

  useEffect(() => {
    widthOfSearchedOrder.current = searchBoxRef.current.getBoundingClientRect().width - 90;
  }, []);

  useEffect(() => {
    if (consignmentsSelection?.length > 0) {
      setSearchedOrder(consignmentsSelection);
      // Order names arrive after the popover opens. Open the list once so
      // the user can pick them, then leave it closed when they confirm.
      // Re-opening here put the dropdown back every time the checkmark
      // tried to show the chosen names in the field.
      const isOrderNameFilter =
        (orderType === "purchase" || (orderType === "allocation" && allocation === "purchase")) &&
        headerName === "order name";
      if (isOrderNameFilter && !orderNameListOpened.current) {
        orderNameListOpened.current = true;
        setIsSearchedItemRender(true);
      }
    }
  }, [
    allocation,
    consignmentsSelection,
    headerName,
    orderType,
    supplier,
    warehouse,
    products,
    transferOrdersSelection,
    stockPacksSelection,
  ]);

  const handleSearch = e => {
    const query = e.target.value;
    setIsSearchedItemRender(true);
    if (query.length > 0) {
      const searchedItems = consignmentsSelection?.filter(name =>
        String(name[selectFilterFieldForHeader[headerName] || "name"])
          ?.toLowerCase()
          .includes(query.toLowerCase()),
      );
      setSearchedOrder(searchedItems);

      setSelectedSearchOrder(searchedItems || []);
      setIsSelectedAllSearchOrder(true);
    } else {
      if (!(consignmentsSelection.length === selectedSearchOrder.length))
        setIsSelectedAllSearchOrder(false);
      setSearchedOrder(consignmentsSelection);
    }
  };

  const handleOrderCheckbox = e => {
    if (e.target.tagName !== "INPUT" || e.target.type !== "checkbox") return;

    if (e.target.value === "selectAll") {
      if (e.target.checked) {
        setIsSelectedAllSearchOrder(true);
        setSelectedSearchOrder([...searchedOrder]);
      } else {
        setIsSelectedAllSearchOrder(false);
        setSelectedSearchOrder([]);
      }
      return;
    }

    const val = JSON.parse(e.target.value);
    if (e.target.checked) {
      setSelectedSearchOrder([...selectedSearchOrder, val]);
    } else {
      setSelectedSearchOrder(selectedSearchOrder.filter(name => name.id !== val.id));
      if (isSelectedAllSearchOrder) {
        setIsSelectedAllSearchOrder(false);
      }
    }
  };

  const handleCloseOfSearch = () => {
    setSelectedSearchOrder([]);
    setSearchedOrder(consignmentsSelection);
  };

  const afterItemSelectedRenderInContainer = () => {
    let elements = ``;
    if (
      selectedSearchOrder.length >= 2 &&
      (selectedSearchOrder[0][selectFilterFieldForHeader[headerName]]?.length +
        selectedSearchOrder[1][selectFilterFieldForHeader[headerName]]?.length) *
        7 <
        widthOfSearchedOrder.current
    ) {
      elements = (
        <>
          <div className="searchedTextBox" onClick={() => setIsSearchedItemRender(true)}>
            <span className="searchedText">
              {selectedSearchOrder[0][selectFilterFieldForHeader[headerName] || "name"]}
            </span>
            <span className="searchedText">
              {selectedSearchOrder[1][selectFilterFieldForHeader[headerName] || "name"]}
            </span>
            {selectedSearchOrder.length > 2 && (
              <span className="searchedText">+{selectedSearchOrder.length - 2}</span>
            )}
          </div>

          <div className="btnGroup">
            <Search onClick={() => setIsSearchedItemRender(true)} />
            <Close onClick={handleCloseOfSearch} />
          </div>
        </>
      );
    } else {
      elements = (
        <>
          <div className="searchedTextBox" onClick={() => setIsSearchedItemRender(true)}>
            <span className="searchedText">
              {selectedSearchOrder[0][selectFilterFieldForHeader[headerName] || "name"]}
            </span>
            {selectedSearchOrder.length !== 1 && (
              <span className="searchedText">
                +{selectedSearchOrder.length === 2 ? 1 : selectedSearchOrder.length - 2}
              </span>
            )}
          </div>
          <div className="btnGroup">
            <Search onClick={() => setIsSearchedItemRender(true)} />
            <Close onClick={handleCloseOfSearch} />
          </div>
        </>
      );
    }
    return elements;
  };

  const isCheckedBox = name => {
    return Boolean(selectedSearchOrder.find(item => item.id === name));
  };

  const applyFilters = () => {
    let paramsData = {};
    if (orderType === "purchase" && headerName === "order name") {
      paramsData = { ids: selectedSearchOrder.map(item => item.id).join(",") };
    } else if (
      orderType === "allocation" &&
      allocation === "purchase" &&
      headerName === "order name"
    ) {
      paramsData = { order_ids: purchaseOrderIdsFromNameOptions(selectedSearchOrder) };
    } else if (orderType === "purchase" && headerName === "payment status") {
      paramsData = {
        payment_status_ids: selectedSearchOrder.map(item => item.id).join(","),
      };
    } else if (orderType === "purchase" && headerName === "po total") {
      paramsData = {
        total_amount_in: selectedSearchOrder.map(item => item.id).join(","),
      };
    } else if (orderType === "purchase" && headerName === "payments") {
      paramsData = {
        total_payments_allocated_in: selectedSearchOrder.map(item => item.id).join(","),
      };
    } else if (orderType === "purchase" && headerName === "outstanding") {
      paramsData = {
        payment_outstanding_in: selectedSearchOrder.map(item => item.id).join(","),
      };
    } else if (orderType === "purchase" && headerName === "shipments") {
      paramsData = {
        consignment_ids: selectedSearchOrder.map(item => item.id).join(","),
      };
    } else if (orderType === "product") {
      switch (headerName) {
        case "created by":
          paramsData = { created_by_ids: selectedSearchOrder.map(item => item.id).join(",") };
          break;

        case "categories":
        case "name":
        case "number":
        case "status":
        case "tags":
        case "type": {
          const field = headerName.replace(/\s+/g, "_");
          const nameFields = ["categories", "status", "tags", "type"];
          paramsData = {
            [`${field}_ids`]: selectedSearchOrder
              .map(item => item[nameFields.includes(field) ? "name" : field])
              .filter(Boolean)
              .join(","),
          };
          break;
        }

        default:
          return;
      }
    } else if (
      headerName === "receipt status" ||
      (["receipt", "receiptAdjustment"].includes(orderType) && headerName === "status")
    ) {
      paramsData = { overall_status: selectedSearchOrder.map(item => item.id).join(",") };
    } else if (orderType === "receipt") {
      switch (headerName) {
        case "created by":
          paramsData = { created_by_ids: selectedSearchOrder.map(item => item.id).join(",") };
          break;

        case "document number":
        case "shipment number": {
          const field = headerName.replace(/\s+/g, "_");
          paramsData = {
            [`${field}_ids`]: selectedSearchOrder
              .map(item => item[field])
              .filter(Boolean)
              .join(","),
          };
          break;
        }

        case "receipt no":
          paramsData = { ids: selectedSearchOrder.map(item => item.id).join(",") };
          break;

        case "shipment":
          paramsData = { consignment_ids: selectedSearchOrder.map(item => item.id).join(",") };
          break;

        case "to facility":
          paramsData = { to_warehouse_id_in: selectedSearchOrder.map(item => item.id).join(",") };
          break;

        default:
          return;
      }
    } else if (["name", "type"].includes(headerName) && orderType === "stockZone") {
      switch (headerName) {
        case "name":
          paramsData = { names: selectedSearchOrder.map(item => item.name).join(",") };
          break;
        case "type":
          paramsData = { type_ids: selectedSearchOrder.map(item => item.id).join(",") };
          break;

        default:
          break;
      }
    } else if (orderType === "delivery" && headerName === "delivery no") {
      switch (headerName) {
        case "delivery no":
          paramsData = { ids: selectedSearchOrder.map(item => item.id).join(",") };
          break;
        default:
          return [];
      }
    } else if (headerName === "name" && orderType === "shipment") {
      paramsData = { name_in: selectedSearchOrder.map(item => item.id).join(",") };
    } else if (headerName === "status" && orderType === "shipment") {
      paramsData = {
        status_code_in: selectedSearchOrder
          .map(item => item.id)
          .filter(Boolean)
          .join(","),
      };
    } else if (
      orderType === "receiptAdjustment" &&
      ["receipt adjustment no", "shipment no", "receipt no"].includes(headerName)
    ) {
      const paramKey = {
        "receipt adjustment no": "ids",
        "shipment no": "consignment_ids",
        "receipt no": "adjusted_from_ids",
      };
      paramsData = { [paramKey[headerName]]: selectedSearchOrder.map(item => item.id).join(",") };
    } else if (orderType === "pack" && headerName === "scope") {
      selectedSearchOrder.forEach(item => {
        paramsData[item.id] = true;
      });
    } else if (headerName === "shipment no" && orderType === "shipmentV1") {
      paramsData = { names: selectedSearchOrder.map(item => item.name).join(",") };
    } else if (headerName === "shipment no" && orderType === "asn") {
      paramsData = {
        shipment_names: selectedSearchOrder.map(item => item.name || item).join(","),
      };
    } else if (headerName === "asn no" && orderType === "asn") {
      paramsData = { names: selectedSearchOrder.map(item => item.name || item).join(",") };
    } else if (headerName === "status" && orderType === "asn") {
      paramsData = { status_ids: selectedSearchOrder.map(item => item.id).join(",") };
    } else if (headerName === "supplier" && orderType === "asn") {
      paramsData = {
        supplier_ids: selectedSearchOrder.map(item => item.id).join(","),
      };
    } else if (headerName === "from facility" && orderType === "asn") {
      paramsData = {
        from_warehouse_ids: selectedSearchOrder.map(item => item.id).join(","),
      };
    } else if (headerName === "to facility" && orderType === "asn") {
      paramsData = {
        to_warehouse_ids: selectedSearchOrder.map(item => item.id).join(","),
      };
    } else if (headerName === "shipment no") {
      paramsData = { reference_in: selectedSearchOrder.map(item => item.id).join(",") };
    } else if (headerName === "purchase no" || headerName === "transfer no") {
      paramsData = { id: selectedSearchOrder.map(item => item.id).join(",") };
    } else if (headerName === "purchase orders" || headerName === "transfer orders") {
      const fieldMapping = {
        "purchase orders": "purchase_order_ids",
        "transfer orders": "transfer_order_ids",
      };
      paramsData = {
        [fieldMapping[headerName]]: selectedSearchOrder.map(item => item.id).join(","),
      };
    } else if (headerName === "supplier" && orderType === "purchase") {
      paramsData = {
        supplier_id: selectedSearchOrder.map(item => item.id).join(","),
      };
    } else if (
      headerName === "supplier" &&
      [
        "allocation",
        "productAlias",
        "pack",
        "warehouseCostingRange",
        "warehouseCosting",
        "productPlan",
        "bill",
        "supplierCreditNote",
        "prePayment",
      ].includes(orderType)
    ) {
      paramsData = {
        supplier_ids: selectedSearchOrder.map(item => item.id).join(","),
      };
    } else if (headerName === "supplier") {
      paramsData = { supplier_id_in: selectedSearchOrder.map(item => item.id).join(",") };
    } else if (
      headerName === "from facility" &&
      [
        "allocation",
        "pack",
        "warehouseCostingRange",
        "warehouseCosting",
        "productPlan",
        "shipmentV1",
      ].includes(orderType)
    ) {
      paramsData = {
        from_warehouse_ids: selectedSearchOrder.map(item => item.id).join(","),
      };
    } else if (
      headerName === "facility" &&
      orderType === "allocation" &&
      allocation === "salesOrder"
    ) {
      paramsData = {
        from_warehouse_ids: selectedSearchOrder.map(item => item.id).join(","),
      };
    } else if (headerName === "from facility" && orderType === "transfer") {
      paramsData = {
        from_warehouse_id: selectedSearchOrder.map(item => item.id).join(","),
      };
    } else if (
      headerName === "to facility" &&
      [
        "allocation",
        "pack",
        "warehouseCostingRange",
        "warehouseCosting",
        "productPlan",
        "customerReturn",
      ].includes(orderType)
    ) {
      paramsData = {
        to_warehouse_ids: selectedSearchOrder.map(item => item.id).join(","),
      };
    } else if (headerName === "to facility" && orderType === "transfer") {
      paramsData = {
        to_warehouse_id: selectedSearchOrder.map(item => item.id).join(","),
      };
    } else if (headerName === "from facility") {
      paramsData = { from_warehouse_id_in: selectedSearchOrder.map(item => item.id).join(",") };
    } else if (headerName === "to facility") {
      paramsData = { to_warehouse_id_in: selectedSearchOrder.map(item => item.id).join(",") };
    } else if (headerName === "facility" && orderType === "purchase") {
      paramsData = {
        warehouse_id: selectedSearchOrder.map(item => item.id).join(","),
      };
    } else if (
      headerName === "facility" &&
      [
        "inventory",
        "inventoryDetail",
        "inventoryTransactions",
        "productAlias",
        "stockZone",
        "forecast",
        "standardCost",
        "stockReplenishment",
        "stockLocation",
        "picklist",
        "manufacturingOrder",
      ].includes(orderType)
    ) {
      paramsData = {
        warehouse_ids: selectedSearchOrder.map(item => item.id).join(","),
      };
    } else if (headerName === "facility") {
      paramsData = {
        warehouse: selectedSearchOrder.map(item => item.id).join(","),
      };
    } else if (headerName === "rep status" && orderType === "salesRep") {
      paramsData = { rep_statuses: selectedSearchOrder.map(item => item.id).join(",") };
    } else if (headerName === "status" && orderType === "purchase") {
      paramsData = { status: selectedSearchOrder.map(item => item.name).join(",") };
    } else if (headerName === "type" && orderType === "purchase") {
      paramsData = { types: selectedSearchOrder.map(item => item.id).join(",") };
    } else if (
      headerName === "status" &&
      (orderType === "allocation" || orderType === "transfer")
    ) {
      paramsData = { status: selectedSearchOrder.map(item => item.id).join(",") };
    } else if (headerName === "status" && orderType === "inventoryDetail") {
      paramsData = {
        status: selectedSearchOrder.map(item => item.id).join(","),
      };
    } else if (
      SALE_INVOICE_FULFILLMENT_HEADERS.includes(headerName) &&
      orderType === "saleInvoice"
    ) {
      if (selectedSearchOrder.length) {
        paramsData = {
          issue_types: selectedSearchOrder.map(item => item.id).join(","),
        };
      }
    } else if (
      headerName === "status" &&
      [
        "bill",
        "syncHistory",
        "stockReplenishment",
        "picklist",
        "stockPack",
        "delivery",
        "saleInvoice",
        "shipmentV1",
        "boms",
        "prePayment",
        "manufacturingOrder",
        "coManufacturingOrder",
      ].includes(orderType)
    ) {
      paramsData = { status_ids: selectedSearchOrder.map(item => item.id).join(",") };
    } else if (headerName === "consolidation status") {
      paramsData = { consolidation_status: selectedSearchOrder.map(item => item.id).join(",") };
    } else if (headerName === "created by") {
      let nameOfPassedData = "created_by_id_in";
      if (orderType === "asn") {
        nameOfPassedData = "created_by_ids";
      } else if (
        [
          "productCategory",
          "productTag",
          "warehouse",
          "productAlias",
          "supplier",
          "pack",
          "stockZone",
          "stockLocation",
          "forecast",
          "warehouseCosting",
          "warehouseCostingRange",
          "productPlan",
          "transactionReason",
          "inventoryDetail",
          "standardCost",
          "paymentTerm",
          "journalLine",
          "taxRate",
          "equipmentType",
          "equipment",
          "unitOfMeasure",
          "site",
          "workCenter",
          "people",
          "coManufacturingOrder",
          "salesRep",
          "billChargeType",
        ].includes(orderType)
      )
        nameOfPassedData = "created_by_ids";
      paramsData = { [nameOfPassedData]: selectedSearchOrder.map(item => item.id).join(",") };
    } else if (
      orderType === "workCenter" &&
      ["location", "facility", "stock zone", "type"].includes(headerName)
    ) {
      const paramKey = {
        location: "location_ids",
        facility: "warehouse_ids",
        "stock zone": "zone_ids",
        type: "type_ids",
      };
      paramsData = { [paramKey[headerName]]: selectedSearchOrder.map(item => item.id).join(",") };
    } else if (orderType === "unitOfMeasure" && ["name", "code", "category"].includes(headerName)) {
      const paramKey = {
        name: "names",
        code: "codes",
        category: "categories",
      };
      paramsData = { [paramKey[headerName]]: selectedSearchOrder.map(item => item.id).join(",") };
    } else if (orderType === "equipmentType" && ["name", "code", "category"].includes(headerName)) {
      const paramKey = {
        name: "names",
        code: "codes",
        category: "categories",
      };
      paramsData = { [paramKey[headerName]]: selectedSearchOrder.map(item => item.id).join(",") };
    } else if (orderType === "equipment" && ["equipment type", "number"].includes(headerName)) {
      const paramKey = {
        "equipment type": "equipment_type_ids",
        number: "numbers",
      };
      paramsData = { [paramKey[headerName]]: selectedSearchOrder.map(item => item.id).join(",") };
    } else if (headerName === "category") {
      paramsData = { product_category_ids: selectedSearchOrder.map(item => item.id).join(",") };
    } else if (orderType === "inventory" && headerName === "categories") {
      paramsData = {
        product_category_ids: selectedSearchOrder.map(item => item.id).join(","),
      };
    } else if (getZeroFilterParam(orderType, headerName)) {
      if (selectedSearchOrder?.length === 1) {
        paramsData = {
          [getZeroFilterParam(orderType, headerName)]:
            selectedSearchOrder[0]?.id === "zero",
        };
      }
    } else if (orderType === "customer") {
      const fieldMapping = {
        "display name": "display_names",
        type: "types",
        code: "customer_codes",
        status: "statuses",
        email: "emails",
        "sub type": "sub_types",
        tags: "tag_ids",
        "sales rep": "sales_rep_ids",
        "default payment term": "default_payment_term_ids",
        "default sale account": "default_sale_account_ids",
        "default tax rate": "default_tax_rate_ids",
      };
      const queryParam = fieldMapping[headerName];
      if (queryParam) {
        paramsData = { [queryParam]: selectedSearchOrder.map(item => item.id).join(",") };
      }
    } else if (orderType === "customerSegment") {
      const fieldMapping = {
        "customer segment name": "names",
        status: "statuses",
      };
      const queryParam = fieldMapping[headerName];
      if (queryParam) {
        paramsData = { [queryParam]: selectedSearchOrder.map(item => item.id).join(",") };
      }
    } else if (headerName === "reason") {
      let fieldName = "id";
      if (["transactionReason", "inventoryTransactions"].includes(orderType)) fieldName = "reason";
      paramsData = { reason_ids: selectedSearchOrder.map(item => item[fieldName]).join(",") };
    } else if (headerName === "impact") {
      paramsData = { impact_ids: selectedSearchOrder.map(item => item.id).join(",") };
    } else if (headerName === "ord no") {
      paramsData = {
        order_ids: selectedSearchOrder.map(item => item.id).join(","),
      };
    } else if (headerName === "erp supplier") {
      paramsData = {
        erp_supplier_names: selectedSearchOrder.map(item => item.id).join("|"),
      };
    } else if (orderType === "pricelist") {
      const fieldMapping = {
        "pricelist name": "name",
        "validity date": "validity_date",
        country: "country_codes",
        channel: "channels_ids",
        "supplying facility": "supply_warehouse_ids",
        status: "status",
      };
      const queryParam = fieldMapping[headerName];
      if (queryParam) {
        paramsData = { [queryParam]: selectedSearchOrder.map(item => item.id).join(",") };
      }
    } else if (
      orderType !== "shopifyCatalogue" &&
      (headerName === "product" || headerName === "product name" || headerName === "description")
    ) {
      paramsData = {
        product_ids: selectedSearchOrder.map(item => item.id).join(","),
      };
    } else if (headerName === "country" && ["warehouse"].includes(orderType)) {
      paramsData = {
        countries: selectedSearchOrder.map(item => item.id).join(","),
      };
    } else if (["productCategory", "productTag", "warehouse"].includes(orderType)) {
      let nameOfPassedData = "ids";
      if (headerName === "parent category") nameOfPassedData = "parent_ids";
      if (headerName === "code") nameOfPassedData = "codes";
      if (orderType === "warehouse" && headerName === "types") nameOfPassedData = "type_ids";
      if (orderType === "warehouse" && headerName === "site") nameOfPassedData = "site_ids";
      paramsData = {
        [nameOfPassedData]: selectedSearchOrder.map(item => item.id).join(","),
      };
    } else if (orderType === "productAlias" && ["name", "number"].includes(headerName)) {
      paramsData = {
        [`${headerName}s`]: selectedSearchOrder.map(item => item.name).join(","),
      };
    } else if (
      ["supplier", "stockLocation"].includes(orderType) &&
      ["name", "code"].includes(headerName)
    ) {
      paramsData = {
        [`${headerName}s`]: selectedSearchOrder.map(item => item.name).join(","),
      };
    } else if (["buying currency", "selling currency", "currency"].includes(headerName)) {
      let nameOfQuery = headerName.split(" ")[0] + "_currency_ids";
      if (headerName === "currency") nameOfQuery = "currency_ids";
      paramsData = {
        [nameOfQuery]: selectedSearchOrder.map(item => item.id).join(","),
      };
    } else if (["acquisition type", "planning policy"].includes(headerName)) {
      const paramKeys = {
        "acquisition type": "acquisition_code",
        "planning policy": "planning_policy",
      };
      paramsData = {
        [`${paramKeys[headerName]}_ids`]: selectedSearchOrder.map(item => item.id).join(","),
      };
    } else if (headerName === "stock zone" && orderType === "stockLocation") {
      paramsData = {
        stock_zone_ids: selectedSearchOrder.map(item => item.id).join(","),
      };
    } else if (headerName === "code" && orderType === "transactionReason") {
      paramsData = {
        code_ids: selectedSearchOrder.map(item => item.code).join(","),
      };
    } else if (orderType === "chartsOfAccount" && ["name", "code"].includes(headerName)) {
      const selectParamKey = { name: "names", code: "codes" };
      paramsData = {
        [selectParamKey[headerName]]: selectedSearchOrder.map(item => item[headerName]).join(","),
      };
    } else if (orderType === "chartsOfAccount" && ["account type"].includes(headerName)) {
      paramsData = {
        type_names: selectedSearchOrder.map(item => item.name).join(","),
      };
    } else if (
      orderType === "journalLine" &&
      ["account code", "account name"].includes(headerName)
    ) {
      paramsData = {
        account_ids: selectedSearchOrder.map(item => item.id).join(","),
      };
    } else if (["system default", "co manufacturer", "default", "active"].includes(headerName)) {
      if (selectedSearchOrder.length === 1) {
        let paramName = "is_default";
        if (headerName === "co manufacturer") paramName = "is_co_manufacturer";
        if (headerName === "active") paramName = "is_active";
        paramsData = {
          // Stringify so URLSearchParams / django BooleanFilter stay consistent.
          [paramName]: String(selectedSearchOrder[0].id),
        };
      } else paramsData = {};
    } else if (orderType === "taxRate" && ["name", "jurisdiction"].includes(headerName)) {
      const selectParamKey = {
        name: { param: "names", fieldName: "name" },
        jurisdiction: { param: "jurisdiction_ids", fieldName: "id" },
      };
      paramsData = {
        [selectParamKey[headerName].param]: selectedSearchOrder
          .map(item => item[selectParamKey[headerName].fieldName])
          .join(","),
      };
    } else if (orderType === "customer") {
      const fieldMapping = {
        "display name": "display_names",
        type: "types",
        code: "customer_codes",
        status: "statuses",
        email: "emails",
      };
      const queryParam = fieldMapping[headerName];
      if (queryParam) {
        paramsData = { [queryParam]: selectedSearchOrder.map(item => item.id).join(",") };
      }
    } else if (
      orderType === "paymentTerm" &&
      ["name", "term type", "due days", "month offset"].includes(headerName)
    ) {
      const queryParams = {
        "term type": "term_types",
        "due days": "due_days_list",
        "month offset": "months_offsets",
      };
      if (headerName === "name") {
        paramsData = {
          names: selectedSearchOrder.map(item => item.name).join(","),
        };
      } else {
        paramsData = {
          [queryParams[headerName]]: selectedSearchOrder.map(item => item.id).join(","),
        };
      }
    } else if (headerName === "other costs type") {
      paramsData = {
        landed_cost_type_id: selectedSearchOrder.map(item => item.id).join(","),
      };
    } else if (qtyHeaders.includes(headerName)) {
      if (selectedSearchOrder?.length === 1) {
        paramsData = {
          [serverFieldForQty[headerName]]: selectedSearchOrder[0]?.id === "zero" ? true : false,
        };
      }
    } else if (orderType === "bill" && ["bill no", "supplier reference"].includes(headerName)) {
      const paramKey = {
        "bill no": "names",
        "supplier reference": "reference_nos",
      };
      paramsData = { [paramKey[headerName]]: selectedSearchOrder.map(item => item.name).join(",") };
    } else if (orderType === "syncHistory" && headerName === "application") {
      paramsData = { external_system_codes: selectedSearchOrder.map(item => item.id).join(",") };
    } else if (
      orderType === "supplierCreditNote" &&
      ["credit no", "supplier reference"].includes(headerName)
    ) {
      const paramKey = {
        "credit no": "names",
        "supplier reference": "reference_nos",
      };
      paramsData = { [paramKey[headerName]]: selectedSearchOrder.map(item => item.name).join(",") };
    } else if (
      orderType === "prePayment" &&
      [
        "payment no",
        "prepayment no",
        "purchase orders",
        "account",
        "bank account",
        "reference",
        "payment type",
      ].includes(headerName)
    ) {
      const paramKey = {
        account: "account_ids",
        "bank account": "bank_account_dummy_ids",
        "purchase orders": "purchase_order_ids",
        status: "status_ids",
      };
      if (headerName === "payment no" || headerName === "prepayment no") {
        paramsData = { names: selectedSearchOrder.map(item => item.name).join(",") };
      } else if (headerName === "reference") {
        paramsData = { references: selectedSearchOrder.map(item => item.name).join(",") };
      } else if (headerName === "payment type") {
        paramsData = {
          payment_type_ids: selectedSearchOrder.map(item => item.id).join(","),
        };
      } else if (["purchase orders", "account", "bank account"].includes(headerName)) {
        paramsData = { [paramKey[headerName]]: selectedSearchOrder.map(item => item.id).join(",") };
      }
    } else if (
      headerName === "product tax classification name" &&
      orderType === "productTaxClassification"
    ) {
      paramsData = { names: selectedSearchOrder.map(item => item.name).join(",") };
    } else if (orderType === "stockReplenishment" && headerName === "replenish no") {
      paramsData = { ids: selectedSearchOrder.map(item => item.id).join(",") };
    } else if (orderType === "customerTag" && headerName === "name") {
      paramsData = { names: selectedSearchOrder.map(item => item.name).join(",") };
    } else if (orderType === "promo" && ["status", "title"].includes(headerName)) {
      const queryParam = {
        status: "status",
        title: "ids",
      };
      paramsData = { [queryParam[headerName]]: selectedSearchOrder.map(item => item.id).join(",") };
    } else if (orderType === "quote") {
      const fieldMapping = {
        "quote number": "ids",
        status: "status_ids",
        customer: "customer_ids",
      };
      paramsData = {
        [fieldMapping[headerName]]: selectedSearchOrder.map(item => item.id).join(","),
      };
    } else if (orderType === "salesOrder") {
      const fieldMapping = {
        "sales no": "ids",
        status: "status_ids",
        customer: "customer_ids",
        warehouse: "from_warehouse_ids",
        pricelist: "pricelist_ids",
        salesperson: "assigned_to_ids",
        currency: "currency_ids",
      };
      if (fieldMapping[headerName]) {
        paramsData = {
          [fieldMapping[headerName]]: selectedSearchOrder.map(item => item.id).join(","),
        };
      }
    } else if (orderType === "saleInvoice" && ["invoice no"].includes(headerName)) {
      paramsData = {
        names: selectedSearchOrder.map(item => item.id).join(","),
      };
    } else if (orderType === "people" && ["name"].includes(headerName)) {
      paramsData = {
        full_names: selectedSearchOrder.map(item => item.id).join(","),
      };
    } else if (orderType === "people" && ["role"].includes(headerName)) {
      paramsData = {
        role_ids: selectedSearchOrder.map(item => item.id).join(","),
      };
    } else if (orderType === "users" && headerName === "role") {
      paramsData = {
        role_ids: selectedSearchOrder.map(item => item.id).join(","),
      };
    } else if (orderType === "users" && headerName === "status") {
      paramsData = {
        status: selectedSearchOrder.map(item => item.id).join(","),
      };
    } else if (orderType === "shopifyCatalogue") {
      switch (headerName) {
        case "product":
          paramsData = {
            product: selectedSearchOrder
              .map(item => item.name || item.id)
              .filter(Boolean)
              .join(","),
            offset: 0,
          };
          break;
        case "status":
          paramsData = {
            listed: selectedSearchOrder.map(item => item.id).filter(Boolean)[0] || "",
            offset: 0,
          };
          break;
        case "available stock":
          paramsData = {
            available_stock: selectedSearchOrder.map(item => item.id).filter(Boolean)[0] || "",
            offset: 0,
          };
          break;
        case "pricelist":
          paramsData = {
            pricelist: selectedSearchOrder
              .map(item => item.name || item.id)
              .filter(Boolean)
              .join(","),
            offset: 0,
          };
          break;
        case "allow backorder":
          paramsData = {
            allow_backorder: selectedSearchOrder.map(item => item.id).filter(Boolean)[0] || "",
            offset: 0,
          };
          break;
        case "facilities":
          paramsData = {
            warehouses: selectedSearchOrder
              .map(item => item.name || item.id)
              .filter(Boolean)
              .join(","),
            offset: 0,
          };
          break;
        default:
          return;
      }
    } else if (orderType === "shipmentLeg") {
      if (["type", "shipment name", "status", "transport mode"].includes(headerName)) {
        const paramsMap = {
          type: "leg_type_ids",
          "shipment name": "shipment_ids",
          status: "status_ids",
          "transport mode": "transport_mode_ids",
        };
        paramsData = {
          [paramsMap[headerName]]: selectedSearchOrder.map(item => item.id).join(","),
        };
      } else {
        const multiSelectFields = {
          carrier: "carriers",
          origin: "origins",
          destination: "destinations",
          "deliver to": "deliver_tos",
          "vehicle / vessel": "vehicle_references",
          sequence: "sequences",
        };
        if (multiSelectFields[headerName]) {
          paramsData = {
            [multiSelectFields[headerName]]: selectedSearchOrder.map(item => item.id).join(","),
          };
        }
      }
    } else if (headerName === "status") {
      paramsData = { status_code_in: selectedSearchOrder.map(item => item.id).join(",") };
    } else if (["tax category name", "tax category"].includes(headerName)) {
      if (orderType === "taxCategory") {
        paramsData = {
          names: selectedSearchOrder.map(item => item.name).join(","),
        };
      } else if (["taxRate", "productTaxClassification"].includes(orderType)) {
        paramsData = {
          tax_category_ids: selectedSearchOrder.map(item => item.id).join(","),
        };
      }
    } else if (headerName === "full name") {
      paramsData = { full_names: selectedSearchOrder.map(item => item.full_name).join(",") };
    } else if (orderType === "billChargeType") {
      switch (headerName) {
        case "name":
          paramsData = { names: selectedSearchOrder.map(item => item.name).join(",") };
          break;
        case "amount type":
          paramsData = { amount_types: selectedSearchOrder.map(item => item.id).join(",") };
          break;
        case "distribution type":
          paramsData = {
            distribution_types: selectedSearchOrder.map(item => item.id).join(","),
          };
          break;
        case "is landed cost":
        case "is landed cost only":
          if (selectedSearchOrder.length === 1) {
            paramsData = { is_landed_cost: String(selectedSearchOrder[0].id) };
          }
          break;
        case "created by":
          paramsData = { created_by_ids: selectedSearchOrder.map(item => item.id).join(",") };
          break;
        default:
          break;
      }
    } else if (selectedHeadersForQueryParams.includes(headerName)) {
      let queryParam = queryParamsName[headerName];
      paramsData = { [queryParam]: selectedSearchOrder.map(item => item.id).join(",") };
    }

    setConsignmentsParamsLocal(state => {
      return { ...state, ...paramsData, offset: 0 };
    });
    setIsAppliedFilter(state => {
      return {
        ...state,
        [headerName]: { isOpen: false, isApplied: true, paramKeys: Object.keys(paramsData) },
      };
    });
  };

  const handleKeyDown = event => {
    if (event.key === "Enter") {
      event.preventDefault();
      applyFilters();
    }
  };

  const toTitleCase = useMemo(
    () =>
      headerName
        ?.split(" ")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" "),
    [headerName],
  );

  return (
    <PopupModal styleOfModal={{ display: "contents" }} closeModal={closeFilterModal}>
      <FilterModalCss className="filterModal" style={positionForFilter}>
        <h3>
          {" "}
          Filter {toTitleCase} <Close onClick={closeFilterModal} />{" "}
        </h3>

        <SearchContainerCss ref={searchBoxRef} className="searchContainer">
          {!isSearchedItemRender && selectedSearchOrder?.length ? (
            afterItemSelectedRenderInContainer()
          ) : (
            <>
              <input
                onChange={handleSearch}
                onClick={() => setIsSearchedItemRender(true)}
                type="text"
                onKeyDown={handleKeyDown}
                placeholder={toTitleCase}
                autoFocus
              />
              <div className="searchIcon">
                {selectedSearchOrder?.length > 0 ? (
                  <Check onClick={() => setIsSearchedItemRender(!isSearchedItemRender)} />
                ) : (
                  <Search />
                )}
              </div>
            </>
          )}
          {isSearchedItemRender && searchedOrder?.length > 0 && (
            <PopupModal closeModal={() => setIsSearchedItemRender(false)}>
              <div onClick={handleOrderCheckbox} className="searchedOrder">
                <label className="searchedItem">
                  <input
                    type="checkbox"
                    checked={searchedOrder.length === selectedSearchOrder.length}
                    onChange={() => ""}
                    value="selectAll"
                    name="selectAll"
                    id=""
                  />
                  Select All
                </label>
                {searchedOrder?.map(name => (
                  <label key={name.id} className="searchedItem" name="label">
                    <input
                      type="checkbox"
                      value={JSON.stringify(name)}
                      checked={isCheckedBox(name.id)}
                      id=""
                      onChange={() => ""}
                    />
                    {name[selectFilterFieldForHeader[headerName]] || name?.name}
                  </label>
                ))}
              </div>
            </PopupModal>
          )}
        </SearchContainerCss>
        <div className="btnGrp">
          <button onClick={closeFilterModal} className="btnForModal">
            Cancel
          </button>
          <button
            className={
              selectedSearchOrder?.length
                ? "btnForModal applyBtn activeBtn"
                : "btnForModal applyBtn"
            }
            onClick={applyFilters}
            disabled={selectedSearchOrder?.length === 0}
          >
            Apply Filter
          </button>
        </div>
      </FilterModalCss>
    </PopupModal>
  );
}

export const FilterModalCss = styled.div`
  width: 20vw;
  border: 1px solid #ecf1f4;
  border-radius: 4px;
  font-size: 14px;
  position: absolute;
  top: 3rem;
  background-color: white;
  z-index: 1000;

  > h3 {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 13px 9px 16px;
    background-color: #f9fafd;
    border: 1px solid #e9edf5;
    font-weight: 600;
    line-height: 24px;
    color: #0e0e2c;
  }

  .btnGrp {
    display: flex;
    justify-content: end;
    font-weight: 500;
    font-size: 14px;
    line-height: 22px;
    height: 32px;
    gap: 10px;
    margin: 0 17px 16px 0;

    > .btnForModal {
      padding: 4px 15px;
      background-color: #f7f8f9;
      border-radius: 4px;
      border: 1px solid transparent;
    }
    .applyBtn {
      color: rgba(0, 0, 0, 0.25);
    }
    .activeBtn {
      background-color: #2060ea;
      color: white;
    }
  }
`;

export const SearchContainerCss = styled.div`
  margin: 19px 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid #c5c7d0;
  border-radius: 4px;
  padding: 4px;
  padding-left: 12px;
  height: 24px;
  position: relative;

  svg {
    cursor: pointer;
  }

  > .searchedTextBox {
    display: flex;
    gap: 9px;
    > .searchedText {
      color: var(--GREY, #4a4a68);
      text-align: center;
      font-feature-settings:
        "liga" off,
        "clig" off;
      font-family: Inter;
      font-size: 12px;
      font-style: normal;
      font-weight: 400;
      line-height: 20px; /* 166.667% */
      background-color: #f4ddde;
      height: 16px;
      padding: 0px 8px 0px 4px;
      display: flex;
      align-items: center;
      border-radius: 4px;
    }
  }

  > input {
    border: none;
    width: 100%;
    font-size: 12px;
    font-weight: 400;
    color: #b9b9c6;
    &:focus {
      outline: none;
      color: #0e0e2c;
    }
  }

  .searchIcon {
    width: 24px;
  }
  .searchedOrder {
    width: 100%;
    position: absolute;
    background-color: white;
    border: 1px solid salmon;
    left: 0;
    top: 30px;
    padding: 4px 0px;
    border-radius: 4px;
    border: 1px solid var(--PEARL, #ecf1f4);
    background: var(--Conditional-pop-over, #fff);
    height: 17rem;
    overflow-y: scroll;

    /* drop-shadow/0.12+0.8+0.5 */
    box-shadow:
      0px 3px 6px -4px rgba(0, 0, 0, 0.12),
      0px 6px 16px 0px rgba(0, 0, 0, 0.08),
      0px 9px 28px 8px rgba(0, 0, 0, 0.05);

    > .searchedItem {
      display: flex;
      padding: 5px 12px;
      align-items: center;
      gap: 8px;
      align-self: stretch;
      color: var(--GREY, #4a4a68);
      font-family: Inter;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 22px;

      > input {
        width: 16px;
        min-width: 16px;
        height: 16px;
        border-radius: 2px;
        border: 1px solid var(--LIGHT-GREY, #8c8ca1);
      }
    }
  }
`;
